import { createSelector } from 'reselect'
import { RoutePath } from '~/routing'
import { b64ToUuid } from '~/utilities/uuid'

import type { StateWithTimeline } from '../timeline/reducers'

import type {
    JobInfo,
    JobInfoElement,
    JobInfoState,
    StateWithJobInfo,
    StoryJobInfo,
} from './reducer'
import { JobInfoStatus } from './reducer'

const getJobInfoState = (state: StateWithJobInfo): JobInfoState => state.jobInfo

export const getJobInfoElements = createSelector(getJobInfoState, (jobInfo): JobInfoElement[] =>
    Object.keys(jobInfo).map((k) => jobInfo[k]),
)
type JobIDMatchMethod = (match: RegExpMatchArray) => JobID
const makeJobUUIDSearchMethod =
    (mountPoints: DictionaryOf<JobIDMatchMethod>) => (currentURL: string) => {
        return Object.keys(mountPoints).reduce<string | undefined>((existing, url) => {
            const match = currentURL.match(new RegExp(url))
            return match ? mountPoints[url](match) : existing
        }, undefined)
    }
const CaptureWebJobMountPoints: DictionaryOf<JobIDMatchMethod> = {
    [RoutePath.getLoggedInAlbumB64Path('([^/]+)')]: (m) => b64ToUuid(m[1]),
    [RoutePath.getNotLoggedInAlbumPath('([^/]+)')]: (m) => b64ToUuid(m[1]),
    [RoutePath.getShareReceiverPath('([^/]+)')]: (m) => m[1], // TODO add b64ToUuid
    [RoutePath.getAlbumFileCommentsPath('([^/]+)', '.*')]: (m) => b64ToUuid(m[1]),
    [RoutePath.getAlbumCarouselPath('([^/]+)', '.*')]: (m) => b64ToUuid(m[1]),
}

export const getJobUUIDFromCaptureWebURL = makeJobUUIDSearchMethod(CaptureWebJobMountPoints)

export const getJobInfoElement = createSelector(
    getJobInfoState,
    (_state: StateWithJobInfo, jobID: JobID) => jobID,
    (jobInfoState, jobID): JobInfoElement | undefined => jobInfoState[jobID],
)

export const getJobInformation = createSelector(
    getJobInfoElement,
    (jobInfoElement: JobInfoElement | undefined): JobInfo | undefined => {
        return jobInfoElement ? jobInfoElement.jobInfo : undefined
    },
)

export const getStoryJobInformation = createSelector(
    getJobInfoElement,
    (jobInfoElement: JobInfoElement | undefined): StoryJobInfo | undefined => {
        return jobInfoElement && jobInfoElement.jobInfo?.type === 'story'
            ? jobInfoElement.jobInfo
            : undefined
    },
)

export const getJobTitle = createSelector(
    getJobInformation,
    (_state: StateWithJobInfo, _jobID: JobID, defaultText: string) => defaultText,
    (info, defaultText): string => (info && 'title' in info ? info.title : defaultText),
)

export const getStatusOfJob = createSelector(
    getJobInfoElement,
    (jobInfoElement): JobInfoStatus =>
        jobInfoElement ? jobInfoElement.status : JobInfoStatus.PENDING,
)

export const getCreatorOfJob = createSelector(getJobInformation, (jobInfo): JobID | undefined =>
    jobInfo && jobInfo.type !== 'timeline' ? jobInfo.owner : undefined,
)

export const getProvidedPassword = createSelector(
    getJobInfoElement,
    (jobInfoElement): string | undefined => jobInfoElement?.providedPassword,
)

export const isStoryJob = createSelector(
    getJobInformation,
    (jobInfo): boolean | undefined => jobInfo && jobInfo.type === 'story',
)
export const isTimelineJob = (state: StateWithTimeline, jobID: JobID): boolean =>
    state.timeline.timelineJob === jobID

export const isShareJob = createSelector(
    getJobInformation,
    (jobInfo): boolean | undefined => jobInfo?.type === 'share',
)

export const isSharedJob = createSelector(
    getJobInformation,
    (info: JobInfo | undefined): boolean => {
        if (info !== undefined && 'isShared' in info) {
            return info.isShared
        }

        return false
    },
)
